import React, { useState } from 'react';
import SectionInfoBox from '../../../components/SectionInfoBox';

interface AmenitiesSectionProps {
    project: any;
    getImageUrl: (url: string) => string;
}

const AmenitiesSection: React.FC<AmenitiesSectionProps> = ({ project, getImageUrl }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    const globalAmenities = (project.amenities && project.amenities.length > 0 ? project.amenities : [])
        .filter((am: any) => typeof am !== 'string')
        .map((am: any) => ({
            ...am,
            icon: project.amenityIcons?.[am._id] || am.icon
        }));

    const customAmenities = project.customAmenities || [];
    const allAmenities = [...globalAmenities, ...customAmenities];
        
    const MOBILE_LIMIT = 6; // 3 rows of 2 columns
    
    // We render all items but will use CSS or logic to conditionally show them.
    // To strictly follow the user request: Desktop shows all, Mobile shows 6 initially.
    const itemsToShow = isExpanded ? allAmenities : allAmenities; // Always show all in data, we'll hide with CSS

    return (
        <div id="amenities" className="card border border-gray-200 shadow-sm rounded-2xl mb-4">
            <div className="bg-white py-4 px-6 md:px-8 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-xl md:text-2xl mb-0 font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">Amenities</h4>
                <SectionInfoBox text={project.sectionInfo?.amenities} align="right" />
            </div>
            <div className="p-6">
                <div className="flex flex-wrap -mx-3 text-center">
                    {allAmenities.map((am: any, i: number) => {
                        const isHiddenOnMobile = !isExpanded && i >= MOBILE_LIMIT;
                        return (
                            <div 
                                className={`w-1/2 md:w-1/4 px-3 md:px-6 ${isHiddenOnMobile ? 'hidden md:block' : 'block'}`} 
                                key={i}
                            >
                                <div className="amenity-item mb-2">
                                    <div className="bg-gray-50/80 w-20 h-20 rounded-full inline-flex mb-3 hover-scale transition-all amenity-icon-box border border-gray-100 shadow-sm" style={{ alignItems: 'center', justifyContent: 'center' }}>
                                        {am.icon ? (
                                            <img 
                                                src={getImageUrl(am.icon)} 
                                                style={{ width: '50px', height: '50px', objectFit: 'contain' }} 
                                                alt={am.name} 
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    if (target.parentElement) {
                                                        const icon = document.createElement('i');
                                                        icon.className = 'fa-solid fa-star text-primary text-3xl';
                                                        target.parentElement.appendChild(icon);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <i className="fa-solid fa-check text-primary text-3xl"></i>
                                        )}
                                    </div>
                                    <p className="mb-0 text-[12px] font-bold text-black">{am.name}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {allAmenities.length > MOBILE_LIMIT && (
                    <div className={`mt-6 flex justify-center md:hidden`}>
                        <button 
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] rounded-full text-[12px] font-black transition-all shadow-lg active:scale-95 border border-[#f1ae24]/30"
                        >
                            <span>{isExpanded ? 'View Less' : `View More (${allAmenities.length - MOBILE_LIMIT}+)`}</span>
                            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px]`}></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AmenitiesSection;
